import jwtDefaultConfig from './jwtDefaultConfig'

export default class JwtService {
  // Will be used by this service for making API calls
  axiosIns = null

  // jwtConfig <= Will be used by this service
  jwtConfig = { ...jwtDefaultConfig }

  // For Refreshing Token
  isAlreadyFetchingAccessToken = false

  // For Refreshing Token
  subscribers = []

  constructor(axiosIns, jwtOverrideConfig) {
    this.axiosIns = axiosIns
    this.jwtConfig = { ...this.jwtConfig, ...jwtOverrideConfig }

    // Request Interceptor
    this.axiosIns.interceptors.request.use(
      config => {
        // Get token from localStorage
        const accessToken = this.getToken()

        // If token is present add it to request's Authorization Header
        if (accessToken) {
          // eslint-disable-next-line no-param-reassign
          config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
        }
        return config
      },
      error => Promise.reject(error),
    )

    // Add request/response interceptor
    this.axiosIns.interceptors.response.use(
      response => response,
      error => {
        const { config } = error
        const originalRequest = config

        if (!this.shouldRefresh(error)) {
          return Promise.reject(error)
        }

        // ลองยิงซ้ำได้ครั้งเดียว — ได้ token ใหม่แล้วยังไม่ผ่านแปลว่า error จริง ไม่ใช่ token หมดอายุ
        originalRequest.retriedAfterRefresh = true

        if (!this.isAlreadyFetchingAccessToken) {
          this.isAlreadyFetchingAccessToken = true

          const sentRefreshToken = this.getRefreshToken()

          this.refreshToken()
            .then(r => {
              this.isAlreadyFetchingAccessToken = false

              // Update accessToken in localStorage
              this.setToken(r.data.accessToken)
              this.setRefreshToken(r.data.refreshToken)
              this.syncUserDataToken(r.data.accessToken)

              this.onAccessTokenFetched(r.data.accessToken)
            })
            .catch(() => {
              this.isAlreadyFetchingAccessToken = false

              /*
               * refresh token ใช้ได้ครั้งเดียว (API หมุนตัวใหม่ทุกครั้ง) ถ้าเปิดหลายแท็บแล้ว token
               * หมดอายุพร้อมกัน แท็บที่รีเฟรชก่อนจะทำให้ตัวที่แท็บนี้ส่งไปใช้ไม่ได้ ถ้าใน localStorage
               * มี refresh token ตัวใหม่แล้ว แปลว่าอีกแท็บรีเฟรชให้แล้ว ใช้ token ของมันยิงซ้ำ ไม่ต้องเตะออก
               */
              const currentRefreshToken = this.getRefreshToken()
              const currentAccessToken = this.getToken()

              if (currentRefreshToken && currentRefreshToken !== sentRefreshToken && currentAccessToken) {
                this.onAccessTokenFetched(currentAccessToken)
                return
              }

              // หมด session จริง — ปล่อย request ที่รออยู่ให้ fail ทั้งหมด แล้วพาไปหน้า login
              this.onAccessTokenFetched('')
              localStorage.removeItem(this.jwtConfig.storageTokenKeyName)
              localStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName)
              localStorage.removeItem('userData')
              window.location.href = '/login'
            })
        }

        return new Promise((resolve, reject) => {
          this.addSubscriber(accessToken => {
            if (!accessToken) {
              reject(error)
              return
            }

            // Make sure to assign accessToken according to your response.
            // Check: https://pixinvent.ticksy.com/ticket/2413870
            // Change Authorization header
            originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
            resolve(this.axiosIns(originalRequest))
          })
        })
      },
    )
  }

  /*
   * error แบบไหนควรลองขอ token ใหม่
   *   401                          -> access token หมดอายุ/ถูก revoke (api/middlewares/auth.middleware.js)
   *   403 "Access token invalid"   -> token ที่ API ตรวจไม่ผ่าน เช่น token เก่าที่ค้างใน localStorage
   *   403 อื่น ๆ เช่น "Admin access required" คือไม่มีสิทธิ์ ขอ token ใหม่ก็ไม่ช่วย
   *
   * ⚠️ ห้ามรีเฟรชเมื่อ request ที่ fail คือตัวรีเฟรชเอง ('/jwt/refresh-token' ของ mock และ
   * 'users/v1/refresh-token' ที่ mock เรียกต่อ) — เดิม API ตอบ refresh token ที่ตายแล้วด้วย 401
   * แล้ว 401 นั้นวนกลับมาต่อคิวรอการรีเฟรชที่ยังไม่จบของตัวเอง promise เลยไม่มีวันจบ
   * หน้าจอค้างแทนที่จะไปหน้า login
   */
  shouldRefresh(error) {
    const { config, response } = error || {}

    if (!response || !config) return false
    if (config.retriedAfterRefresh) return false

    const url = String(config.url || '')
    if (url.includes(this.jwtConfig.refreshEndpoint) || url.includes('users/v1/refresh-token')) return false

    if (response.status === 401) return true

    return response.status === 403 && (response.data && response.data.message) === 'Access token invalid'
  }

  onAccessTokenFetched(accessToken) {
    this.subscribers = this.subscribers.filter(callback => callback(accessToken))
  }

  addSubscriber(callback) {
    this.subscribers.push(callback)
  }

  getToken() {
    return localStorage.getItem(this.jwtConfig.storageTokenKeyName)
  }

  getRefreshToken() {
    return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
  }

  setToken(value) {
    localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
  }

  setRefreshToken(value) {
    localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)
  }

  /*
   * อัปเดต userData.token ให้ตรงกับ accessToken ตัวใหม่ทุกครั้งที่รีเฟรชสำเร็จ
   *
   * ⚠️ ระบบเก็บ token ของ API ไว้ 2 ที่ ที่มาจากตอนล็อกอินก้อนเดียวกัน
   *      localStorage.accessToken   -> interceptor ด้านบนใช้ใส่ Authorization ให้ @axios
   *      localStorage.userData.token -> หน้า analytics อ่านตัวนี้ (ดู views/analytics/analyticsApi.js
   *                                     ที่ต้องใช้ axios instance เปล่า เลยไม่ได้ผ่าน interceptor)
   *
   * เดิมรีเฟรชแล้วเขียนแค่ accessToken ทำให้ userData.token ค้างอยู่ที่ค่าตอนล็อกอินตลอด
   * access token มีอายุ 2 ชั่วโมง (controllers/usr.controller.js -> generateToken)
   * ครบ 2 ชั่วโมงหลังล็อกอิน หน้า analytics จึงพังยาวจนกว่าจะออกจากระบบแล้วเข้าใหม่
   * ทั้งที่ส่วนอื่นของระบบยังใช้งานได้ปกติเพราะมันรีเฟรชให้เงียบ ๆ อยู่แล้ว
   *
   * ห้าม throw ออกไป — ตัวนี้ถูกเรียกกลางสายรีเฟรช ถ้าพังจะลาก .catch() ให้เตะผู้ใช้
   * ออกไปหน้า login ทั้งที่รีเฟรชสำเร็จแล้ว
   */
  // eslint-disable-next-line class-methods-use-this
  syncUserDataToken(accessToken) {
    if (!accessToken) return

    try {
      const raw = localStorage.getItem('userData')

      // ไม่มี userData = ยังไม่ได้ล็อกอิน ไม่ต้องสร้างก้อนใหม่ที่มีแค่ token
      if (!raw) return

      const userData = JSON.parse(raw)

      if (!userData || typeof userData !== 'object') return

      userData.token = accessToken
      localStorage.setItem('userData', JSON.stringify(userData))
    } catch (error) {
      // userData เพี้ยน/พาร์สไม่ได้ — ปล่อยผ่าน หน้า analytics จะขึ้น error ของมันเองอยู่แล้ว
    }
  }

  login(...args) {
    return this.axiosIns.post(this.jwtConfig.loginEndpoint, ...args)
  }

  register(...args) {
    return this.axiosIns.post(this.jwtConfig.registerEndpoint, ...args)
  }

  refreshToken() {
    return this.axiosIns.post(this.jwtConfig.refreshEndpoint, {
      refreshToken: this.getRefreshToken(),
    })
  }
}
